import { useMemo } from 'react';
import { logoUrl } from '../api/client';
import { TRACKER_IDS, TRACKER_NAMES, type Payload, type Reading, type WindowKey } from '../api/types';
import { count, pct, usd } from '../lib/format';
import { buildStories, W_LABEL } from '../lib/stories';
import { HBars, finite } from '../ui/charts';
import { GlassHBars, SmoothLines } from '../ui/glass';
import { Icon } from '../ui/Icon';
import { Logo } from '../ui/Logo';
import { Card, Cover, Rich } from './Reports';

/* Every figure a tracker can publish for a casino, for the full table. */
const FIELDS: { key: keyof Reading; label: string; fmt: (v: number) => string }[] = [
  { key: 'value', label: 'Deposits', fmt: usd },
  { key: 'withdrawals', label: 'Withdrawals', fmt: usd },
  { key: 'netFlow', label: 'Net flow', fmt: usd },
  { key: 'count', label: 'Deposit count', fmt: count },
  { key: 'depositors', label: 'Depositors', fmt: count },
  { key: 'avgDeposit', label: 'Average deposit', fmt: usd },
  { key: 'largestDeposit', label: 'Largest deposit', fmt: usd },
  { key: 'share', label: 'Market share', fmt: (v) => pct(v) },
  { key: 'rank', label: 'Rank', fmt: (v) => `#${v}` },
];

type Pt = { day: string; usd: number };

export function Report({ data, win, id }: { data: Payload; win: WindowKey; id: string }) {
  const stories = useMemo(() => buildStories(data, win), [data, win]);
  const story = stories.find((s) => s.id === id);

  if (!story) {
    return (
      <div className="tile">
        <p className="empty">That story is not in the current collection for this window. <a href={`/reports?w=${win}`}>All reports</a></p>
      </div>
    );
  }

  const row = story.casinoKey ? data.analysis.volume[win]?.rows.find((r) => r.key === story.casinoKey) : undefined;
  const daily = row
    ? TRACKER_IDS.map((tid) => {
      const raw = row.extra?.[tid]?.daily;
      const pts = Array.isArray(raw) ? raw.filter((p): p is Pt => !!p && typeof p.day === 'string' && finite(p.usd)) : [];
      return { key: tid, name: TRACKER_NAMES[tid], color: `var(--tracker-${tid})`, points: pts.map((p) => ({ day: p.day.slice(0, 10), value: p.usd })) };
    }).filter((s) => s.points.length >= 2)
    : [];
  // Related reading: the same kind first, then the rest, so a disagreement
  // leads on to other disagreements.
  const more = [
    ...stories.filter((s) => s.id !== story.id && s.kind === story.kind),
    ...stories.filter((s) => s.id !== story.id && s.kind !== story.kind),
  ].slice(0, 3);
  const a = data.analysis.volume[win];
  const meta = row ? TRACKER_IDS.map((tid) => row.extra?.[tid]?.meta).find((m) => m && (m.license || m.foundedYear)) : undefined;
  const logo = row?.logos && Object.keys(row.logos).length ? logoUrl(row.key) : null;
  const fields = row ? FIELDS.filter((f) => TRACKER_IDS.some((tid) => finite(row.by[tid]?.[f.key]))) : [];
  const shown = row ? TRACKER_IDS.filter((tid) => row.reportedBy.includes(tid)) : [];
  // The market story's detail: the largest casinos, each tracker a dot on the median bar.
  const largest = story.kind === 'market' && a ? a.rows.filter((r) => finite(r.consensus)).slice(0, 10) : [];
  const collected = new Date(data.scrapedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const [lede, second, ...rest] = story.paragraphs;

  return (
    <article className="story">
      <header className="story-cover">
        <Cover story={story} />
        <div className="story-cover-copy">
          <span className="story-kicker"><i className="live-dot" aria-hidden />{story.kicker}</span>
          <h1 className="story-title">{story.title}</h1>
        </div>
      </header>

      <div className="story-narrow">
        <div className="story-head">
          <p className="story-dek">{story.dek}</p>
          <div className="story-byline">
            <span><b>HouseGap</b></span>
            <span>From the {collected} collection</span>
            <span>{story.minutes} min read</span>
          </div>
        </div>

        <ul className="story-stats">
          {story.stats.map((s) => <li key={s.label}><b>{s.value}</b><small>{s.label}</small></li>)}
        </ul>

        {row && (
          <section className="tile story-facts">
            <div className="story-facts-id">
              <Logo src={logo} name={row.name} className="phead-logo" />
              <div>
                <span className="phead-kicker">About the casino</span>
                <h2>{row.name}</h2>
                <ul className="story-facts-meta">
                  <li><Icon name="shield" size={16} />Licence <b>{meta?.license ?? 'not stated'}</b></li>
                  <li><Icon name="calendar" size={16} />Founded <b>{meta?.foundedYear ?? 'not stated'}</b></li>
                  <li><Icon name="rings" size={16} />Listed by <b>{row.reportedBy.length} of {TRACKER_IDS.length}</b> trackers</li>
                </ul>
              </div>
            </div>
            <ul className="story-facts-ranks">
              <li><small>Median deposits</small><b>{usd(row.consensus)}</b><span>last {W_LABEL[win]}</span></li>
              {shown.map((tid) => {
                const rank = row.by[tid]?.rank;
                return (
                  <li key={tid}>
                    <small><i className="gdot" style={{ background: `var(--tracker-${tid})` }} aria-hidden />{TRACKER_NAMES[tid]}</small>
                    <b>{finite(rank) ? `#${rank}` : '—'}</b>
                    <span>{finite(rank) ? 'its rank' : 'rank not published'}</span>
                  </li>
                );
              })}
            </ul>
            <div className="phead-actions">
              <a className="btn btn-primary btn-md" href={`/casino?c=${row.key}&w=${win}`}>Open the full profile</a>
              <a className="btn btn-secondary btn-md" href={`/matchup?c=${row.key}&w=${win}`}>Compare side by side</a>
            </div>
          </section>
        )}

        <div className="tile story-body">
          {lede && <p className="lede"><Rich text={lede} /></p>}
          {second && <p><Rich text={second} /></p>}
          {story.pull && <blockquote className="pull">{story.pull}</blockquote>}

          {story.readings.length >= 2 && (
            <figure className="fig">
              <div className="fig-title">What each tracker reported</div>
              <div className="fig-note">
                {story.kind === 'balance' ? 'Hot-wallet balance, right now.' : `Deposits over the last ${W_LABEL[win]}.`} Hollow marks are reference-only.
              </div>
              <div className="fig-chart">
                <GlassHBars items={story.readings.map((r) => ({
                  key: r.id, value: r.value, display: usd(r.value), color: `var(--tracker-${r.id})`, label: TRACKER_NAMES[r.id],
                  sub: [r.counted ? '' : 'reference only', finite(r.wallets) ? `${r.wallets} wallets` : ''].filter(Boolean).join(' · '),
                }))} />
              </div>
            </figure>
          )}

          {rest.map((p, i) => <p key={i}><Rich text={p} /></p>)}

          {story.windows && story.windows.length >= 2 && (
            <figure className="fig">
              <div className="fig-title">Does the gap hold?</div>
              <div className="fig-note">How far apart the counted trackers are, in every window they both publish.</div>
              <div className="fig-chart">
                <GlassHBars items={story.windows.map((w) => ({
                  key: w.w, label: W_LABEL[w.w], value: w.value, display: pct(w.value, 0), className: `g${w.value < 5 ? 0 : w.value < 15 ? 1 : w.value < 40 ? 2 : 3}`,
                }))} />
              </div>
            </figure>
          )}

          {daily.length > 0 && (
            <figure className="fig">
              <div className="fig-title">Day by day</div>
              <div className="fig-note">Daily deposits, as each tracker that publishes a daily series reports them.</div>
              <div className="fig-chart"><SmoothLines series={daily} format={usd} height={220} /></div>
            </figure>
          )}

          {row && fields.length > 0 && (
            <figure className="fig">
              <div className="fig-title">Every figure the trackers published</div>
              <div className="fig-note">{row.name}, last {W_LABEL[win]}. A dash means that tracker does not publish the figure: missing is not zero.</div>
              <div className="t-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th>Figure</th>
                      {shown.map((tid) => <th key={tid} className="r"><i className="gdot" style={{ background: `var(--tracker-${tid})`, display: 'inline-block', marginRight: 8, width: 8, height: 8 }} aria-hidden />{TRACKER_NAMES[tid]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f) => (
                      <tr key={f.key}>
                        <td>{f.label}</td>
                        {shown.map((tid) => {
                          const v = row.by[tid]?.[f.key];
                          return <td key={tid} className="r">{finite(v) ? f.fmt(v) : <span className="muted">—</span>}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </figure>
          )}

          {largest.length > 0 && (
            <figure className="fig">
              <div className="fig-title">The largest casinos, tracker by tracker</div>
              <div className="fig-note">The bar is each casino&rsquo;s median and each dot is one tracker&rsquo;s figure. Where the dots spread, the trackers disagree.</div>
              <div className="fig-chart">
                <HBars items={largest.map((r) => ({
                  key: r.key, href: `/casino?c=${r.key}&w=${win}`, label: r.name, value: r.consensus ?? 0, display: usd(r.consensus),
                  dots: TRACKER_IDS.flatMap((tid) => {
                    const v = r.by[tid]?.value;
                    return finite(v) ? [{ key: tid, value: v, color: `var(--tracker-${tid})`, title: `${TRACKER_NAMES[tid]} ${usd(v)}`, hollow: !r.consensusBy.includes(tid) }] : [];
                  }),
                }))} />
              </div>
            </figure>
          )}

          <aside className="method">
            <h3>How this was made</h3>
            <ul>
              <li>Written automatically from the {collected} HouseGap collection. It updates every hour.</li>
              <li>Figures are what each tracker published. HouseGap does not measure casinos itself and does not decide which tracker is right.</li>
              <li>Apart means the highest counted figure minus the lowest, over their median. Reference-only trackers are shown but not counted.</li>
            </ul>
          </aside>
        </div>

        <div className="story-foot">
          <a href={`/reports?w=${win}`}>← All reports</a>
          {story.casinoKey && <a href={`/casino?c=${story.casinoKey}&w=${win}`}>{story.casinoName}’s full profile →</a>}
        </div>
      </div>

      {more.length > 0 && (
        <section className="more-reports">
          <h2>More reports</h2>
          <ul className="report-list">
            {more.map((s) => <li key={s.id}><Card story={s} win={win} /></li>)}
          </ul>
        </section>
      )}
    </article>
  );
}
